"use client"

import React, { useMemo, useState } from "react"
import { FLOW_ROWS, FLOW_UNSECTIONED_KEY, ribbonPath, type FlowLink, type MappingFlow } from "@/lib/mapping-flow"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface MappingFlowCanvasProps {
  flow: MappingFlow
  highlighted: string | null
  onHover: (link: FlowLink | null) => void
  onSelect: (link: FlowLink) => void
}

const ENTITY_COLORS = ["#9db2e0", "#6cc496", "#f0b35a", "#b9a6e8", "#7fd0c4", "#e8a58b"]
const INVALID = "#e57373"
const VALUE_SET = "#f0cd8a"
const MUTED = "#6b7a9a"
const TEXT = "#e7ebf5"

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

/**
 * The mapping flow as a scrollable SVG: ATS fields down the left, FBO destinations down
 * the right with section headers, value-mapping sets as junctions, and a ribbon per
 * mapping. Hovering a ribbon brings it forward; invalid ribbons are red; unmapped
 * destinations sit grey so gaps read at a glance.
 */
export function MappingFlowCanvas({ flow, highlighted, onHover, onSelect }: MappingFlowCanvasProps) {
  const { t, n } = useTranslation()
  const [hover, setHover] = useState<string | null>(null)
  const entityColor = useMemo(() => new Map(flow.stats.entities.map((e, i) => [e, ENTITY_COLORS[i % ENTITY_COLORS.length]])), [flow.stats.entities])
  const sourceById = useMemo(() => new Map(flow.sources.map((s) => [s.id, s])), [flow.sources])
  const targetById = useMemo(() => new Map(flow.targets.map((t) => [t.id, t])), [flow.targets])
  const junctionById = useMemo(() => new Map(flow.junctions.map((j) => [j.id, j])), [flow.junctions])

  const active = hover ?? highlighted
  const emphasised = (id: string) => active === null || active === id
  const touched = useMemo(() => {
    const ids = new Set<string>()
    const link = flow.links.find((l) => l.id === active)
    if (link) [link.sourceId, link.targetId, link.junctionId].forEach((id) => id && ids.add(id))
    return ids
  }, [flow.links, active])
  const rowEmphasised = (id: string) => active === null || touched.has(id)

  // Destination paths name their own section; only the no-prefix fallback carries a key
  const sectionLabel = (section: string) => (section === FLOW_UNSECTIONED_KEY ? t(FLOW_UNSECTIONED_KEY) : section)

  const enter = (link: FlowLink) => {
    setHover(link.id)
    onHover(link)
  }
  const leave = () => {
    setHover(null)
    onHover(null)
  }

  const { sourceX, targetX, width, labelChars } = flow.geometry
  const { row } = FLOW_ROWS
  const ribbons = flow.links
    .map((link) => ({ link, s: sourceById.get(link.sourceId), t: targetById.get(link.targetId), j: link.junctionId ? junctionById.get(link.junctionId) ?? null : null }))
    .filter((r) => r.s && r.t)
    // The active ribbon paints last so it sits on top
    .sort((a, b) => Number(a.link.id === active) - Number(b.link.id === active))

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      {/* Column headers: shaded band that stays put while the rows scroll */}
      <div className="sticky top-0 z-10 h-8 border-b border-white/10 bg-[#0b1735]/85 text-[11px] text-[#c7d2f0] backdrop-blur-sm" style={{ width }}>
        <span className="absolute top-2 -translate-x-full whitespace-nowrap pr-2.5" style={{ left: sourceX }}>
          <span className="font-semibold text-[#e7ebf5]">{t("mappingFlow.canvas.sourceHeader")}</span>
          {flow.stats.entities.length > 0 && <span className="text-[#9db2e0]"> · {flow.stats.entities.join(", ")}</span>}
        </span>
        <span className="absolute left-1/2 top-2 -translate-x-1/2 text-[#6b7a9a]">→</span>
        <span className="absolute top-2 whitespace-nowrap pl-2.5 font-semibold text-[#e7ebf5]" style={{ left: targetX }}>
          {t("mappingFlow.canvas.targetHeader")}
        </span>
      </div>
      <svg width={width} height={flow.height} className="block font-mono" role="img" aria-label={t("mappingFlow.blade.title")}>
        {flow.sections.map((section) => (
          <text key={section.section} x={section.x + 8} y={section.y + 11} fontSize="9" fontWeight={700} letterSpacing="0.08em" fill="#9db2e0" className="font-sans uppercase">
            {sectionLabel(section.section)}
          </text>
        ))}

        {ribbons.map(({ link, s, t, j }) => {
          const strong = emphasised(link.id)
          const color = link.invalid ? INVALID : link.valueSet ? VALUE_SET : entityColor.get(s!.entity) ?? ENTITY_COLORS[0]
          return (
            <g key={link.id} onMouseEnter={() => enter(link)} onMouseLeave={leave} onClick={() => onSelect(link)} className="cursor-pointer">
              <path d={ribbonPath(s!, t!, j)} fill="none" stroke="transparent" strokeWidth={12} />
              <path d={ribbonPath(s!, t!, j)} fill="none" stroke={color} strokeWidth={strong ? 2.5 : 1.5} strokeOpacity={strong ? 0.95 : 0.22} strokeLinecap="round" className="transition-[stroke-opacity,stroke-width] duration-150" />
            </g>
          )
        })}

        {flow.sources.map((s) => (
          <g key={s.id} opacity={rowEmphasised(s.id) ? 1 : 0.35} className="transition-opacity duration-150">
            <circle cx={s.x} cy={s.y} r={4} fill={entityColor.get(s.entity) ?? ENTITY_COLORS[0]} />
            <text x={s.x - 10} y={s.y + 3.5} textAnchor="end" fontSize="11" fill={TEXT}>
              <title>{`${s.entity}.${s.field}`}</title>
              <tspan fill="#9db2e0">{s.entity}.</tspan>
              {truncate(s.field, labelChars - s.entity.length - 1)}
            </text>
          </g>
        ))}

        {flow.targets.map((target) => {
          const label = target.collapsedCount
            ? t((target.collapsedCount === 1 ? "mappingFlow.canvas.unmappedCountOne" : "mappingFlow.canvas.unmappedCountOther"), { count: n(target.collapsedCount) })
            : target.field
          return (
            <g key={target.id} opacity={rowEmphasised(target.id) || !target.mapped ? 1 : 0.35} className="transition-opacity duration-150">
              {target.collapsedCount ? (
                <rect x={target.x - 4} y={target.y - row / 2 + 3} width={12 + label.length * 6.6} height={row - 6} rx={8} fill="#1d2f60" />
              ) : (
                <circle cx={target.x} cy={target.y} r={4} fill={target.invalid ? INVALID : target.mapped ? "#ffffff" : "#3f4d6f"} />
              )}
              <text x={target.x + 10} y={target.y + 3.5} fontSize="11" fill={target.invalid ? INVALID : target.mapped ? TEXT : MUTED} fontStyle={target.collapsedCount ? "italic" : undefined}>
                <title>{label}</title>
                {target.collapsedCount ? label : truncate(label.replace(`${target.section}.`, ""), labelChars)}
              </text>
            </g>
          )
        })}

        {flow.junctions.map((j) => (
          <g key={j.id} opacity={rowEmphasised(j.id) ? 1 : 0.35} className="transition-opacity duration-150">
            <rect x={j.x - 7} y={j.y - 7} width={14} height={14} rx={3} transform={`rotate(45 ${j.x} ${j.y})`} fill={VALUE_SET} />
            <text x={j.x} y={j.y - 13} textAnchor="middle" fontSize="10" fontWeight={600} fill={VALUE_SET} className="font-sans">
              {j.valueSet}
            </text>
          </g>
        ))}

        {/* Column guides */}
        <line x1={sourceX} y1={0} x2={sourceX} y2={flow.height} stroke="#ffffff" strokeOpacity={0.05} />
        <line x1={targetX} y1={0} x2={targetX} y2={flow.height} stroke="#ffffff" strokeOpacity={0.05} />
      </svg>
    </div>
  )
}
